import { useState, useEffect } from 'react';
import { useAppSelector } from '../../../reducers/hooks';
import { ColumnConfig, SortDirection } from '../DynamicTable';

const compareSortValues = (
  valueA: string | number | boolean | Date | null | undefined,
  valueB: string | number | boolean | Date | null | undefined,
  language: string,
) => {
  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA - valueB;
  }

  if (valueA instanceof Date && valueB instanceof Date) {
    return valueA.getTime() - valueB.getTime();
  }

  if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
    return Number(valueA) - Number(valueB);
  }

  return String(valueA ?? '').localeCompare(String(valueB ?? ''), language, {
    numeric: true,
    sensitivity: 'base',
  });
};

export interface UseTableSortingProps {
  columnData: ColumnConfig[];
  rowData: any[];
}

export interface UseTableSortingReturn {
  isSorting: boolean;
  displayRowData: any[]; // current data to display (sorted or original)
  sortColumnDataKey: string | undefined;
  sortDirection: SortDirection;
  onColumnHeaderPress: (columnKey: string) => void;
}

export const useTableSorting = ({
  columnData,
  rowData,
}: UseTableSortingProps): UseTableSortingReturn => {
  const { language } = useAppSelector((state) => state.user);

  const [isSorting, setIsSorting] = useState(false);
  const [displayRowData, setDisplayRowData] = useState<any[]>(rowData);
  const [sortColumnDataKey, setSortColumnDataKey] = useState<
    string | undefined
  >(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.Neutral,
  );

  useEffect(() => {
    setDisplayRowData(rowData);
    setSortColumnDataKey(undefined);
    setSortDirection(SortDirection.Neutral);
  }, [rowData]);

  const onColumnHeaderPress = (columnKey: string) => {
    if (isSorting) {
      return;
    }

    let nextDirection: SortDirection;

    if (sortColumnDataKey === columnKey) {
      // If the same column is clicked, toggle the sort direction
      if (sortDirection === SortDirection.Ascending) {
        nextDirection = SortDirection.Descending;
      } else {
        nextDirection = SortDirection.Ascending;
      }
    } else {
      // If a new column is clicked, set it as the sort column and default to ascending
      nextDirection = SortDirection.Ascending;
      setSortColumnDataKey(columnKey);
    }

    setSortDirection(nextDirection);

    const selectedColumn = columnData.find(
      (column) => column.dataKey === columnKey,
    );

    const sortFn = (a: any, b: any) => {
      const valueA = selectedColumn?.getSortValue
        ? selectedColumn.getSortValue(a)
        : a[columnKey];
      const valueB = selectedColumn?.getSortValue
        ? selectedColumn.getSortValue(b)
        : b[columnKey];

      return compareSortValues(valueA, valueB, language);
    };

    setIsSorting(true);
    const sortedData = rowData.toSorted((a, b) =>
      nextDirection === SortDirection.Ascending ? sortFn(a, b) : -sortFn(a, b),
    );

    setDisplayRowData(sortedData);
    setIsSorting(false);
  };

  return {
    isSorting,
    displayRowData,
    sortColumnDataKey,
    sortDirection,
    onColumnHeaderPress,
  };
};
